export { parseCsv } from '@/pages/people/member-import-csv';

export type ExpenseCategoryImportField = 'name' | 'parentCategoryName' | 'isActive';

export const EXPENSE_CATEGORY_IMPORT_FIELDS: ExpenseCategoryImportField[] = [
  'name',
  'parentCategoryName',
  'isActive',
];

export const REQUIRED_EXPENSE_CATEGORY_IMPORT_FIELDS: ExpenseCategoryImportField[] = ['name'];

export const EXPENSE_CATEGORY_IMPORT_FIELD_LABEL_KEYS: Record<
  ExpenseCategoryImportField,
  string
> = {
  name: 'categories.name',
  parentCategoryName: 'categories.import.fields.parentCategory',
  isActive: 'categories.import.fields.active',
};

export type ExpenseCategoryImportRowIssue =
  | 'missingName'
  | 'invalidBoolean'
  | 'duplicateNameInFile'
  | 'duplicateNameExisting'
  | 'parentCategoryNotFound';

export interface ExpenseCategoryImportRowPayload {
  name: string;
  parentCategoryName?: string;
  isActive?: boolean;
}

export interface PreparedExpenseCategoryImportRow {
  index: number;
  source: Record<string, string>;
  payload?: ExpenseCategoryImportRowPayload;
  issues: ExpenseCategoryImportRowIssue[];
}

export const MAX_EXPENSE_CATEGORY_IMPORT_ROWS = 500;

export const SAMPLE_EXPENSE_CATEGORY_CSV = `name,parent category
Utilities,
Travel,Utilities
Office supplies,
`;

const HEADER_ALIASES: Record<ExpenseCategoryImportField, string[]> = {
  name: ['name', 'category', 'category name', 'categoryname', 'category_name', 'expense category'],
  parentCategoryName: [
    'parent',
    'parent category',
    'parentcategory',
    'parent_category',
    'parent name',
    'parentname',
  ],
  isActive: ['active', 'is active', 'isactive', 'is_active', 'status'],
};

const normalizeHeader = (value: string): string =>
  value.replace(/^\uFEFF/, '').trim().toLowerCase().replace(/\s+/g, ' ');

const normalizeName = (value: string): string => value.trim().toLowerCase();

export const guessExpenseCategoryFieldForHeader = (
  header: string,
): ExpenseCategoryImportField | null => {
  const key = normalizeHeader(header);
  if (!key) return null;
  for (const field of EXPENSE_CATEGORY_IMPORT_FIELDS) {
    if (HEADER_ALIASES[field].includes(key) || key === field.toLowerCase()) return field;
  }
  return null;
};

export const buildExpenseCategoryImportMapping = (
  headers: string[],
): Record<string, ExpenseCategoryImportField | ''> => {
  const mapping: Record<string, ExpenseCategoryImportField | ''> = {};
  const used = new Set<ExpenseCategoryImportField>();
  for (const header of headers) {
    const guess = guessExpenseCategoryFieldForHeader(header);
    if (guess && !used.has(guess)) {
      mapping[header] = guess;
      used.add(guess);
    } else {
      mapping[header] = '';
    }
  }
  return mapping;
};

const parseBoolean = (value: string): boolean | null => {
  const normalized = value.trim().toLowerCase();
  if (!normalized) return null;
  if (['yes', 'y', 'true', '1', 'active'].includes(normalized)) return true;
  if (['no', 'n', 'false', '0', 'inactive'].includes(normalized)) return false;
  return null;
};

export const prepareExpenseCategoryImportRows = (input: {
  rows: Record<string, string>[];
  mapping: Record<string, ExpenseCategoryImportField | ''>;
  existingNames: Set<string>;
}): PreparedExpenseCategoryImportRow[] => {
  const seenNames = new Map<string, number>();
  const availableNames = new Set(input.existingNames);

  return input.rows.map((source, index) => {
    const issues: ExpenseCategoryImportRowIssue[] = [];
    const values: Partial<Record<ExpenseCategoryImportField, string>> = {};

    for (const [header, field] of Object.entries(input.mapping)) {
      if (!field) continue;
      values[field] = source[header]?.trim() ?? '';
    }

    const name = values.name?.trim() ?? '';
    if (!name) issues.push('missingName');

    const normalizedName = normalizeName(name);
    if (name) {
      if (seenNames.has(normalizedName)) {
        issues.push('duplicateNameInFile');
      } else {
        seenNames.set(normalizedName, index);
      }
      if (input.existingNames.has(normalizedName)) {
        issues.push('duplicateNameExisting');
      }
    }

    let isActive: boolean | undefined;
    if (values.isActive) {
      const parsedActive = parseBoolean(values.isActive);
      if (parsedActive === null) {
        issues.push('invalidBoolean');
      } else {
        isActive = parsedActive;
      }
    }

    const parentCategoryName = values.parentCategoryName?.trim() ?? '';
    if (parentCategoryName) {
      const normalizedParent = normalizeName(parentCategoryName);
      if (normalizedParent === normalizedName || !availableNames.has(normalizedParent)) {
        issues.push('parentCategoryNotFound');
      }
    }

    if (issues.length > 0) {
      return { index, source, issues };
    }

    if (name) {
      availableNames.add(normalizedName);
    }

    const payload: ExpenseCategoryImportRowPayload = {
      name,
      ...(parentCategoryName ? { parentCategoryName } : {}),
      ...(isActive != null ? { isActive } : {}),
    };

    return { index, source, payload, issues };
  });
};
